import { useState, useEffect, useCallback } from 'react';
import { FileSignature, Plus, Download, Trash2, X, Loader2, ChevronRight, Check, Edit3, Save, RefreshCw, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch, downloadFile } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const SOWBuilder = ({ user }) => {
  // Normalize Google Docs HTML that splits {{placeholders}} across span tags
  const normalizePlaceholders = (html) => {
    const TAG = '(?:\\s*</?span[^>]*>\\s*)*';
    const vars = [
      'client_name', 'project_name', 'scope_of_work', 'start_date', 'end_date',
      'budget', 'timeline', 'payment_terms', 'total_value', 'notes', 'status',
      'id', 'date', 'deliverables', 'deliverables_text'
    ];
    let result = html;
    for (const v of vars) {
      const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`\\{${TAG}\\{${TAG}${escaped}${TAG}\\}${TAG}\\}`, 'g');
      result = result.replace(pattern, `{{${v}}}`);
    }
    return result;
  };

  const substituteVars = (html, sow) => {
    if (!html) return '';
    let result = normalizePlaceholders(html);
    const deliverablesList = (sow.deliverables || []).map(d =>
      `<li><b>${d.name || ''}</b>${d.description ? ': ' + d.description : ''}</li>`
    ).join('');
    const replacements = {
      '{{client_name}}': sow.client_name || '',
      '{{project_name}}': sow.project_name || '',
      '{{scope_of_work}}': sow.scope_of_work || '',
      '{{deliverables}}': deliverablesList ? `<ul>${deliverablesList}</ul>` : 'N/A',
      '{{deliverables_text}}': (sow.deliverables || []).map(d => `- ${d.name}: ${d.description || ''}`).join('\n'),
      '{{start_date}}': sow.start_date || 'TBD',
      '{{end_date}}': sow.end_date || 'TBD',
      '{{budget}}': sow.budget ? `$${Number(sow.budget).toLocaleString()}` : 'TBD',
      '{{timeline}}': sow.timeline || 'TBD',
      '{{payment_terms}}': sow.payment_terms || 'TBD',
      '{{total_value}}': sow.total_value ? `$${Number(sow.total_value).toLocaleString()}` : 'TBD',
      '{{notes}}': sow.notes || '',
      '{{status}}': sow.status || 'Draft',
      '{{id}}': (sow.id || '').slice(0, 8),
      '{{date}}': (sow.created_at || '').slice(0, 10),
    };
    for (const [key, val] of Object.entries(replacements)) {
      result = result.replaceAll(key, val);
    }
    return result;
  };

  const [sows, setSows] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedSow, setSelectedSow] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectDeliverables, setProjectDeliverables] = useState([]);
  const [newProjectMode, setNewProjectMode] = useState(false);

  const emptyForm = {
    client_name: '', project_id: '', project_name: '', template_type: 'standard',
    scope_of_work: '', deliverables: [],
    start_date: '', end_date: '', budget: '',
    timeline: '', payment_terms: '', total_value: '', notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  // Fetch Drive client folders the user can see (Admin: all, Internal: only `assigned_folders`).
  // Normalize the shape to `{id, name}` so all the existing dropdown logic just works.
  const fetchDriveClients = async () => {
    const res = await authFetch(`${API}/api/drive/clients`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.clients || []).map(c => ({
      id: c.drive_folder_id,
      name: c.folder_name,
      drive_folder_id: c.drive_folder_id,
    }));
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [sowsRes, templatesRes, projRes, drivers] = await Promise.all([
          authFetch(`${API}/api/sow`),
          authFetch(`${API}/api/sow/templates`),
          authFetch(`${API}/api/projects`),
          fetchDriveClients(),
        ]);
        if (sowsRes.ok) setSows((await sowsRes.json()).sow_documents || []);
        if (templatesRes.ok) setTemplates((await templatesRes.json()).templates || {});
        if (projRes.ok) setProjects((await projRes.json()).projects || []);
        setClients(drivers);
      } catch (e) { console.error('Failed to load SOW data:', e); }
      setLoading(false);
    };
    loadInitialData();
     
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sowsRes, templatesRes, projRes, drivers] = await Promise.all([
        authFetch(`${API}/api/sow`),
        authFetch(`${API}/api/sow/templates`),
        authFetch(`${API}/api/projects`),
        fetchDriveClients(),
      ]);
      if (sowsRes.ok) setSows((await sowsRes.json()).sow_documents || []);
      if (templatesRes.ok) setTemplates((await templatesRes.json()).templates || {});
      if (projRes.ok) setProjects((await projRes.json()).projects || []);
      setClients(drivers);
    } catch (e) { console.error('Failed to load SOW data:', e); }
    setLoading(false);
  };

  const loadProjectDeliverables = useCallback(async (projectId) => {
    if (!projectId) { setProjectDeliverables([]); return; }
    try {
      const res = await authFetch(`${API}/api/projects/${projectId}/deliverables`);
      if (res.ok) {
        const data = await res.json();
        setProjectDeliverables(data.deliverables || []);
      }
    } catch (e) { console.error('Failed to load deliverables:', e); }
     
  }, []);

  const onProjectSelect = async (projectId) => {
    if (!projectId || projectId === '_new') {
      setForm(f => ({ ...f, project_id: projectId === '_new' ? '' : '', project_name: '' }));
      setProjectDeliverables([]);
      return;
    }
    const proj = projects.find(p => p.id === projectId);
    if (proj) {
      // Match by drive_folder_id (new model) — projects link to a Drive folder
      // which is also our client identity. Fall back to legacy client_id match.
      const client = clients.find(
        c => c.drive_folder_id === proj.drive_folder_id || c.id === proj.client_id,
      );
      setForm(f => ({
        ...f,
        project_id: proj.id,
        project_name: proj.name,
        client_name: client?.name || proj.client_name || f.client_name,
      }));
      await loadProjectDeliverables(proj.id);
    }
  };

  const toggleDeliverable = (del) => {
    setForm(f => {
      const exists = f.deliverables.some(d => d.name === del.name);
      if (exists) {
        return { ...f, deliverables: f.deliverables.filter(d => d.name !== del.name) };
      }
      return { ...f, deliverables: [...f.deliverables, { name: del.name, description: del.description || '' }] };
    });
  };

  const addCustomDeliverable = () => {
    setForm(f => ({ ...f, deliverables: [...f.deliverables, { name: '', description: '', custom: true }] }));
  };

  const removeDeliverable = (i) => setForm(f => ({ ...f, deliverables: f.deliverables.filter((_, idx) => idx !== i) }));
  const updateDeliverable = (i, key, val) => {
    const copy = [...form.deliverables];
    copy[i] = { ...copy[i], [key]: val };
    setForm(f => ({ ...f, deliverables: copy }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name) { toast.error('Client name required'); return; }

    const payload = {
      ...form,
      budget: form.budget ? parseFloat(form.budget) : null,
      total_value: form.total_value ? parseFloat(form.total_value) : null,
      deliverables: form.deliverables.map(({ custom, ...d }) => d),
    };
    const res = await authFetch(`${API}/api/sow`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success('SOW created');
      setShowForm(false);
      setForm(emptyForm);
      fetchData();
    } else {
      toast.error('Failed to create SOW');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SOW?')) return;
    const res = await authFetch(`${API}/api/sow/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('SOW deleted'); setSelectedSow(null); fetchData(); }
  };

  const exportPdf = async (id) => {
    try {
      await downloadFile(`/api/sow/${id}/export-pdf`, `SOW_${id.slice(0,8)}.pdf`);
    } catch (e) { toast.error('PDF export failed'); }
  };

  const viewSow = async (id) => {
    try {
      const res = await authFetch(`${API}/api/sow/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedSow(data.sow);
        setEditing(false);
        setPreviewVersion(v => v + 1);
        if (data.sow.project_id) {
          await loadProjectDeliverables(data.sow.project_id);
        }
      }
    } catch (e) { toast.error('Failed to load SOW'); }
  };

  const startEditing = () => {
    const sow = selectedSow;
    setForm({
      client_name: sow.client_name || '',
      project_id: sow.project_id || '',
      project_name: sow.project_name || '',
      template_type: sow.template_type || 'standard',
      scope_of_work: sow.scope_of_work || '',
      deliverables: sow.deliverables || [],
      start_date: sow.start_date || '',
      end_date: sow.end_date || '',
      budget: sow.budget || '',
      timeline: sow.timeline || '',
      payment_terms: sow.payment_terms || '',
      total_value: sow.total_value || '',
      notes: sow.notes || '',
    });
    setEditing(true);
  };

  const saveEdits = async () => {
    setSaving(true);
    const payload = {
      project_name: form.project_name,
      scope_of_work: form.scope_of_work,
      deliverables: form.deliverables.map(({ custom, ...d }) => d),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      budget: form.budget ? parseFloat(form.budget) : null,
      timeline: form.timeline || null,
      payment_terms: form.payment_terms || null,
      total_value: form.total_value ? parseFloat(form.total_value) : null,
      notes: form.notes || null,
      status: selectedSow.status,
    };
    try {
      const res = await authFetch(`${API}/api/sow/${selectedSow.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSow(data.sow);
        setEditing(false);
        setPreviewVersion(v => v + 1);
        toast.success('SOW updated — template re-merged');
        fetchData();
      } else { toast.error('Failed to update SOW'); }
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  // ═══════ DETAIL VIEW ═══════
  if (selectedSow) {
    const sow = editing ? { ...selectedSow, ...form, budget: form.budget ? parseFloat(form.budget) : null, total_value: form.total_value ? parseFloat(form.total_value) : null } : selectedSow;
    const hasTemplate = sow.drive_template_html || sow.template_source === 'google_drive';

    return (
      <div data-testid="sow-detail">
        <button onClick={() => { setSelectedSow(null); setEditing(false); }} className="flex items-center gap-1 text-sm text-brand hover:underline mb-4">&larr; Back to SOW List</button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">{templates[sow.template_type]?.name || 'SOW'}</h2>
            <p className="text-sm text-text-secondary">
              {sow.client_name}{sow.project_name ? ` — ${sow.project_name}` : ''} &middot; {sow.id?.slice(0,8)} &middot; {sow.status}
              {sow.template_source === 'google_drive' && <span className="ml-2 text-xs font-medium text-brand bg-brand/10 px-2 py-0.5 rounded-full">Drive Template</span>}
            </p>
          </div>
          <div className="flex gap-2">
            {!editing ? (
              <Button variant="outline" size="sm" onClick={startEditing} data-testid="sow-edit-btn"><Edit3 size={14} className="mr-1" /> Edit</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" className="bg-brand text-white hover:bg-brand-hover" onClick={saveEdits} disabled={saving} data-testid="sow-save-btn">
                  {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Save & Merge
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => exportPdf(sow.id)} data-testid="sow-export-pdf"><Download size={14} className="mr-1" /> PDF</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const url = `${window.location.origin}/internal/sow/${sow.id}`;
                const label = `${sow.client_name}${sow.project_name ? ` — ${sow.project_name}` : ''}`;
                const prefill = `Sharing this SOW — please review:\n*${label}*\n${url}`;
                window.dispatchEvent(new CustomEvent('ventas:slack:dm', {
                  detail: { prefillText: prefill },
                }));
              }}
              className="text-chart-purple border-chart-purple/30 hover:bg-chart-purple/10"
              data-testid="sow-share-slack"
            >
              <MessageSquare size={14} className="mr-1" /> Share
            </Button>
            <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(sow.id)} data-testid="sow-delete"><Trash2 size={14} /></Button>
          </div>
        </div>

        {/* Edit form panel */}
        {editing && (
          <Card className="mb-6 border-brand/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-brand">Edit SOW Fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">Project Name</Label>
                  <Input value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} className="mt-1" data-testid="edit-project-name" />
                </div>
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1" data-testid="edit-start-date" />
                </div>
                <div>
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="mt-1" data-testid="edit-end-date" />
                </div>
                <div>
                  <Label className="text-xs">Budget ($)</Label>
                  <Input type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} className="mt-1" data-testid="edit-budget" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Timeline</Label>
                  <Input value={form.timeline} onChange={e => setForm(f => ({ ...f, timeline: e.target.value }))} className="mt-1" placeholder="e.g. 6 months" />
                </div>
                <div>
                  <Label className="text-xs">Payment Terms</Label>
                  <Input value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="mt-1" placeholder="e.g. Net 30" />
                </div>
                <div>
                  <Label className="text-xs">Total Value ($)</Label>
                  <Input type="number" value={form.total_value} onChange={e => setForm(f => ({ ...f, total_value: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Scope of Work</Label>
                <textarea value={form.scope_of_work} onChange={e => setForm(f => ({ ...f, scope_of_work: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-input rounded-xl bg-white min-h-[80px] text-sm" />
              </div>

              {/* Deliverables from project */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Deliverables</Label>
                  <button type="button" onClick={addCustomDeliverable} className="text-xs text-brand hover:underline">+ Add Custom</button>
                </div>
                {projectDeliverables.length > 0 && (
                  <div className="mb-3 p-3 rounded-lg bg-surface-page border border-border-subtle">
                    <p className="text-xs text-text-secondary mb-2 font-medium">Project Deliverables (click to toggle)</p>
                    <div className="flex flex-wrap gap-2">
                      {projectDeliverables.map((d, i) => {
                        const selected = form.deliverables.some(fd => fd.name === d.name);
                        return (
                          <button key={`item-${i}`} type="button" onClick={() => toggleDeliverable(d)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${selected ? 'bg-brand text-white' : 'bg-white border border-border-subtle text-text-secondary hover:border-brand/40'}`}
                            data-testid={`proj-del-${i}`}
                          >
                            {selected && <Check size={12} />} {d.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {form.deliverables.map((d, i) => (
                  <div key={`item-${i}`} className="flex gap-2 mb-2">
                    <Input placeholder="Deliverable name" value={d.name} onChange={e => updateDeliverable(i, 'name', e.target.value)} className="flex-1" />
                    <Input placeholder="Description" value={d.description} onChange={e => updateDeliverable(i, 'description', e.target.value)} className="flex-1" />
                    <button type="button" onClick={() => removeDeliverable(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-input rounded-xl bg-white min-h-[60px] text-sm" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Drive template rendered with variables (live preview) */}
        {hasTemplate && sow.drive_template_html && (
          <Card className="mb-6">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-secondary">
                {editing ? 'Live Preview' : 'SOW Document (from Google Drive Template)'}
              </CardTitle>
              {editing && (
                <Button variant="outline" size="sm" onClick={() => setPreviewVersion(v => v + 1)} data-testid="refresh-preview-btn">
                  <RefreshCw size={14} className="mr-1" /> Refresh Preview
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg bg-white overflow-auto max-h-[70vh]" data-testid="sow-template-preview">
                <iframe
                  key={previewVersion}
                  srcDoc={substituteVars(sow.drive_template_html, sow)}
                  title="SOW Preview"
                  className="w-full border-0"
                  style={{ minHeight: '600px', height: '70vh' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fallback: manual fields when no Drive template */}
        {!hasTemplate && !editing && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card><CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Scope of Work</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-slate-700 whitespace-pre-wrap">{sow.scope_of_work}</p></CardContent></Card>
              <Card><CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Deliverables</CardTitle></CardHeader>
                <CardContent>{(sow.deliverables || []).map((d, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-surface-page border border-border-subtle mb-2">
                    <ChevronRight size={14} className="text-brand mt-0.5" /><div><p className="text-sm font-medium">{d.name}</p><p className="text-xs text-text-secondary">{d.description}</p></div>
                  </div>))}
                </CardContent></Card>
            </div>
            <div className="space-y-6">
              <Card><CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Details</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {sow.project_name && <div><span className="text-text-secondary">Project:</span> <span className="font-medium">{sow.project_name}</span></div>}
                  {sow.start_date && <div><span className="text-text-secondary">Start:</span> <span className="font-medium">{sow.start_date}</span></div>}
                  {sow.end_date && <div><span className="text-text-secondary">End:</span> <span className="font-medium">{sow.end_date}</span></div>}
                  {sow.budget && <div><span className="text-text-secondary">Budget:</span> <span className="font-medium">${Number(sow.budget).toLocaleString()}</span></div>}
                  <div><span className="text-text-secondary">Timeline:</span> <span className="font-medium">{sow.timeline || 'TBD'}</span></div>
                  <div><span className="text-text-secondary">Payment:</span> <span className="font-medium">{sow.payment_terms || 'TBD'}</span></div>
                  <div><span className="text-text-secondary">Value:</span> <span className="font-medium">${(sow.total_value || 0).toLocaleString()}</span></div>
                </CardContent></Card>
            </div>
          </div>
        )}

        {/* Summary cards */}
        {hasTemplate && !editing && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <Card><CardContent className="p-4"><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Client</p><p className="text-sm font-medium">{sow.client_name}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Dates</p><p className="text-sm font-medium">{sow.start_date || '—'} → {sow.end_date || '—'}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Budget</p><p className="text-sm font-medium">{sow.budget ? `$${Number(sow.budget).toLocaleString()}` : 'TBD'}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Value</p><p className="text-sm font-medium">{sow.total_value ? `$${Number(sow.total_value).toLocaleString()}` : 'TBD'}</p></CardContent></Card>
          </div>
        )}
      </div>
    );
  }

  // ═══════ CREATE FORM (modal) ═══════
  const renderCreateForm = () => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b">
            <h2 className="text-lg font-bold text-text-primary">Create Statement of Work</h2>
            <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-surface-page"><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {/* Project & Client */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project</Label>
                {!newProjectMode ? (
                  <>
                    <select
                      value={form.project_id}
                      onChange={e => {
                        if (e.target.value === '_new') { setNewProjectMode(true); setForm(f => ({ ...f, project_id: '', project_name: '' })); setProjectDeliverables([]); }
                        else onProjectSelect(e.target.value);
                      }}
                      className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white text-sm"
                      data-testid="sow-project-select"
                    >
                      <option value="">Select project...</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      <option value="_new">+ New project name...</option>
                    </select>
                  </>
                ) : (
                  <div className="flex gap-2 mt-1.5">
                    <Input placeholder="New project name" value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} className="flex-1" data-testid="sow-new-project-name" />
                    <button type="button" onClick={() => setNewProjectMode(false)} className="text-xs text-text-tertiary hover:text-text-secondary">Pick existing</button>
                  </div>
                )}
              </div>
              <div>
                <Label>Client</Label>
                <select value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white text-sm" data-testid="sow-client-select" required>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  <option value="_custom">Custom name...</option>
                </select>
                {form.client_name === '_custom' && <Input className="mt-2" placeholder="Client name" onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />}
              </div>
            </div>

            {/* Template, Dates, Budget */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Template</Label>
                <select value={form.template_type} onChange={e => setForm(f => ({ ...f, template_type: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white text-sm" data-testid="sow-template-select">
                  {Object.entries(templates).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1.5" data-testid="sow-start-date" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="mt-1.5" data-testid="sow-end-date" />
              </div>
              <div>
                <Label>Budget ($)</Label>
                <Input type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} className="mt-1.5" placeholder="0" data-testid="sow-budget" />
              </div>
            </div>

            {/* Scope */}
            <div>
              <Label>Scope of Work</Label>
              <textarea value={form.scope_of_work} onChange={e => setForm(f => ({ ...f, scope_of_work: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white min-h-[80px] text-sm" placeholder="Describe the scope..." data-testid="sow-scope" />
            </div>

            {/* Deliverables picker */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Deliverables</Label>
                <button type="button" onClick={addCustomDeliverable} className="text-xs text-brand hover:underline">+ Add Custom</button>
              </div>

              {projectDeliverables.length > 0 && (
                <div className="mb-3 p-3 rounded-lg bg-surface-page border border-border-subtle">
                  <p className="text-xs text-text-secondary mb-2 font-medium">Pick from project deliverables:</p>
                  <div className="flex flex-wrap gap-2">
                    {projectDeliverables.map((d, i) => {
                      const selected = form.deliverables.some(fd => fd.name === d.name);
                      return (
                        <button key={`item-${i}`} type="button" onClick={() => toggleDeliverable(d)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${selected ? 'bg-brand text-white' : 'bg-white border border-border-subtle text-text-secondary hover:border-brand/40'}`}
                          data-testid={`create-proj-del-${i}`}
                        >
                          {selected && <Check size={12} />} {d.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {form.deliverables.map((d, i) => (
                <div key={`item-${i}`} className="flex gap-2 mb-2">
                  <Input placeholder="Deliverable name" value={d.name} onChange={e => updateDeliverable(i, 'name', e.target.value)} className="flex-1" />
                  <Input placeholder="Description" value={d.description} onChange={e => updateDeliverable(i, 'description', e.target.value)} className="flex-1" />
                  <button type="button" onClick={() => removeDeliverable(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
                </div>
              ))}
            </div>

            {/* Extra fields */}
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Timeline</Label><Input value={form.timeline} onChange={e => setForm(f => ({ ...f, timeline: e.target.value }))} placeholder="e.g. 6 months" className="mt-1.5" /></div>
              <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g. Net 30" className="mt-1.5" /></div>
              <div><Label>Total Value ($)</Label><Input type="number" value={form.total_value} onChange={e => setForm(f => ({ ...f, total_value: e.target.value }))} placeholder="0" className="mt-1.5" data-testid="sow-value" /></div>
            </div>
            <div><Label>Notes</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white min-h-[60px] text-sm" /></div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand text-white hover:bg-brand-hover" data-testid="sow-submit">Create SOW</Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ═══════ LIST VIEW ═══════
  return (
    <div data-testid="sow-builder-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-3xl font-semibold tracking-tight text-text-primary">SOW Builder</h1><p className="text-sm text-text-tertiary mt-1">Create and manage statements of work</p></div>
        <Button onClick={() => { setForm(emptyForm); setProjectDeliverables([]); setNewProjectMode(false); setShowForm(true); }} className="bg-brand text-white hover:bg-brand-hover shadow-xs" data-testid="create-sow-btn"><Plus size={16} className="mr-1" /> New SOW</Button>
      </div>

      {sows.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><FileSignature className="mx-auto mb-3 text-text-muted" size={40} /><p className="text-text-secondary">No SOW documents yet</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sows.map(sow => (
            <Card key={sow.id} className="cursor-pointer hover:border-brand/30 transition-colors" onClick={() => viewSow(sow.id)} data-testid={`sow-card-${sow.id?.slice(0,8)}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <FileSignature size={18} className="text-brand" />
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sow.status === 'Draft' ? 'bg-warning/10 text-warning' : sow.status === 'Approved' ? 'bg-success/10 text-success' : 'bg-surface-page text-text-secondary'}`}>{sow.status}</span>
                </div>
                <h3 className="text-base font-bold text-text-primary mt-2">{templates[sow.template_type]?.name || 'SOW'}</h3>
                <p className="text-sm text-text-secondary mt-1">{sow.client_name}{sow.project_name ? ` — ${sow.project_name}` : ''}</p>
                {sow.template_source === 'google_drive' && (
                  <span className="inline-block text-xs font-medium text-brand bg-brand/10 px-2 py-0.5 rounded-full mt-1.5" data-testid="sow-drive-badge">Drive Template</span>
                )}
                {sow.budget && <p className="text-xs text-text-tertiary mt-1">Budget: ${Number(sow.budget).toLocaleString()}</p>}
                {sow.total_value && <p className="text-sm font-semibold text-brand mt-1">${Number(sow.total_value).toLocaleString()}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && renderCreateForm()}
    </div>
  );
};

export default SOWBuilder;
;

;
